import React, { FunctionComponent, ReactNode } from 'react';
import Downshift, { DownshiftProps } from 'downshift';
import { SelectRoot } from './SelectRoot';
import {
	getItemDisplayValueDefault,
	getItemKeyDefault,
	IDropdownItem,
} from '../DropdownList';

export interface ISelectProps<T = IDropdownItem> extends DownshiftProps<T> {
	items: T[];
	itemToKey?: (item: T) => string;
	itemToString?: (item: T | null) => string;
	dropdownItemValue?: (item: T | null) => string | React.JSX.Element;
	placeholder?: string;
	ariaLabel?: string;
	disabled?: boolean;
	invalid?: boolean;
	className?: string;
	icon?: ReactNode;
	defaultScrollToNthItem?: number;
	placeholderAsLabel?: boolean;
	onClick?: () => void;
	withDropdownFloatingPortal?: boolean;
}

export interface ISelectComponent<Props = ISelectProps>
	extends FunctionComponent<Props> {}

export function Select<TProps extends ISelectProps = ISelectProps>({
	items,
	itemToKey,
	itemToString,
	placeholder,
	ariaLabel,
	disabled,
	invalid,
	className,
	icon,
	defaultScrollToNthItem,
	placeholderAsLabel,
	onClick,
	withDropdownFloatingPortal,
	...rest
}: TProps) {
	const getItemKey = itemToKey || getItemKeyDefault;
	const getItemDisplayValue = itemToString || getItemDisplayValueDefault;

	return (
		<Downshift
			itemToString={getItemDisplayValue}
			id="downshift-select"
			{...rest}
		>
			{({ getRootProps, ...props }) => (
				<SelectRoot
					{...getRootProps({ refKey: 'innerRef' })}
					getItemDisplayValue={
						rest.dropdownItemValue
							? rest.dropdownItemValue
							: getItemDisplayValue
					}
					getItemKey={getItemKey}
					items={items}
					disabled={disabled}
					invalid={invalid}
					className={className}
					ariaLabel={ariaLabel ?? placeholder}
					placeholder={placeholder}
					icon={icon}
					defaultScrollToNthItem={defaultScrollToNthItem}
					placeholderAsLabel={placeholderAsLabel}
					onClick={onClick}
					withDropdownFloatingPortal={withDropdownFloatingPortal}
					{...props}
				/>
			)}
		</Downshift>
	);
}
